import type { FieldShortcut } from 'korint-sdk/react';
import {
  Award,
  Cake,
  Calendar,
  Check,
  Globe,
  Hourglass,
  Mail,
  MapPin,
  Phone,
  ShieldCheck,
  Sprout,
  TrendingUp,
  User,
  X,
} from 'lucide-react';

// Only the colours that differ from folio's defaults (usePolicyStatuses merges them over those).
export const policyStatuses = {
  POLICY_CREATED: 'bg-gray-100 text-gray-700',
  QUOTE_TO_BE_APPROVED: 'bg-indigo-100 text-indigo-700',
  DEROGATION_APPROVED: 'bg-indigo-100 text-indigo-700',
};

export const axeceDriverContactFields: Array<FieldShortcut> = [
  { name: 'driverCivility', colSpan: 4 },
  { name: 'driverLastName', required: true, icon: User, colSpan: 4 },
  { name: 'driverFirstName', required: true, icon: User, colSpan: 4 },
  { name: 'driverBirthDate', required: true, icon: Cake, colSpan: 4 },
  {
    name: 'driverBirthCity',
    required: true,
    type: 'city',
    addressGroup: 'driverBirth',
    icon: MapPin,
    colSpan: 4,
  },
  {
    name: 'driverBirthCountry',
    required: true,
    type: 'country',
    addressGroup: 'driverBirth',
    icon: Globe,
    colSpan: 4,
  },
  { name: 'driverEmail', required: true, icon: Mail, colSpan: 6 },
  { name: 'driverPhoneNumber', required: true, icon: Phone, colSpan: 6 },
  { name: 'parkingAddress', required: true, type: 'address', icon: MapPin, colSpan: 6 },
  { name: 'parkingPostcode', required: true, colSpan: 3 },
  { name: 'parkingCity', required: true, type: 'city', colSpan: 3 },
] as const;

export const axeceDriverLicenseFields: Array<FieldShortcut> = [
  {
    name: 'licenseDate',
    required: true,
    max: 'startOfDay-5y',
    icon: Calendar,
    colSpan: 12,
  },
  {
    name: 'background',
    required: true,
    display: 'cards',
    icons: { LESS_THAN_3_YEARS: Hourglass, MORE_THAN_3_YEARS: ShieldCheck },
  },
  {
    name: 'professionalExperience',
    required: true,
    display: 'cards',
    icons: {
      LESS_THAN_1_YEAR: Sprout,
      BETWEEN_1_AND_5_YEARS: TrendingUp,
      MORE_THAN_5_YEARS: Award,
    },
  },
  {
    name: 'CRM',
    required: true,
    maxLength: 4, // max decimal places = 2
    colSpan: 6,
  },
  {
    name: 'numberOfYearBonus50',
    required: true,
    min: 0,
    colSpan: 6,
    showWhen: [
      {
        fieldName: 'CRM',
        condition: 'equals',
        value: 0.5,
      },
    ],
  },
] as const;

export const axeceDriverDeclarationFields = [
  { name: 'noTerminationForAnyReason', required: true, type: 'checkbox' },
  { name: 'noTerminationForNonPayment', required: true, type: 'checkbox' },
  { name: 'noDriverLicenseSuspension', required: true, type: 'checkbox' },
  { name: 'noConvictionForDrunkDriving', required: true, type: 'checkbox' },
] as const satisfies Array<FieldShortcut>;

export const secondDriverOption = {
  name: 'secondDriverOption',
  required: false,
  display: 'cards',
  icons: { true: Check, false: X },
} as const satisfies FieldShortcut;

export function getFieldsByDriver(
  fields: Array<FieldShortcut>,
  driver: string,
): Array<FieldShortcut> {
  return fields.map(field => {
    if (typeof field === 'string') {
      return {
        name: `${field}${driver}`,
      };
    }

    return {
      ...field,
      name: `${field.name}${driver}`,
      showWhen: field.showWhen
        ? field.showWhen.map(condition => ({
            ...condition,
            fieldName: condition.fieldName ? `${condition.fieldName}${driver}` : undefined,
          }))
        : undefined,
    };
  });
}

export const ImaAssistanceComponentIds = ['WK4', '1WK'];

export const baseDrivequantScoreRange = '[8;8.5[';
export const bestDrivequantScoreRange = '>= 9.5';
